import type {
  AppNotification,
  EarningsData,
  Panelist,
  Profile,
  ProfileFieldsData,
  ProfileFieldValues,
  Task,
} from "../types";

export const panelist: Panelist = {
  id: 0,
  firstName: "Maya",
  lastName: "Patel",
  email: "maya.patel@example.com",
  memberSince: "Apr 2026",
  points: 420,
  pointsGbp: "£8.40",
  pointsToNext: null,
  tasksCompleted: 12,
  totalEarnedPts: 1140,
  totalEarnedGbp: "£22.80",
  profilePct: 82,
  isCheckinDue: true,
};

export const tasks: Task[] = [
  {
    uid: "job_001",
    title: "Grocery shopping habits survey",
    category: "Retail",
    metadata: { topic: "Retail", estimatedMins: 8 },
    points: 75,
    gbpEquiv: 1.5,
    tags: ["new"],
  },
  {
    uid: "job_002",
    title: "Streaming services check-in",
    category: "Entertainment",
    metadata: { topic: "Entertainment", estimatedMins: 12 },
    points: 100,
    gbpEquiv: 2,
    tags: ["popular"],
  },
  {
    uid: "job_003",
    title: "Personal finance quick poll",
    category: "Finance",
    metadata: { topic: "Finance", estimatedMins: 6 },
    points: 50,
    gbpEquiv: 1,
    tags: [],
  },
  {
    uid: "job_004",
    title: "Home energy providers study",
    category: "Utilities",
    metadata: { topic: "Utilities", estimatedMins: 15 },
    points: 125,
    gbpEquiv: 2.5,
    tags: ["new"],
  },
];

export const earnings: EarningsData = {
  summary: {
    totalPts: 1140,
    totalGbp: "£22.80",
    pendingPts: 175,
    pendingGbp: "£3.50",
    tasksDone: 12,
  },
  items: [
    {
      id: "earn_001",
      title: "Weekly food shop survey",
      date: "13 May 2026",
      points: 75,
      status: "pending",
    },
    {
      id: "earn_002",
      title: "Banking app feedback",
      date: "09 May 2026",
      points: 100,
      status: "paid",
    },
    {
      id: "earn_003",
      title: "Streaming habits poll",
      date: "06 May 2026",
      points: 50,
      status: "paid",
    },
    {
      id: "earn_004",
      title: "Travel screening survey",
      date: "01 May 2026",
      points: 0,
      status: "failed",
    },
    {
      id: "earn_005",
      title: "Energy supplier diary",
      date: "28 Apr 2026",
      points: 125,
      status: "qa_fail",
    },
  ],
};

export const notifications: AppNotification[] = [
  {
    id: "notif_001",
    title: "New survey available",
    body: "A grocery shopping habits survey is ready for you.",
    createdAt: "Today, 09:20",
    href: "/tasks/job_001",
    kind: "task",
    readAt: null,
  },
  {
    id: "notif_002",
    title: "Points pending review",
    body: "75 pts from Weekly food shop survey are now pending quality review.",
    createdAt: "Yesterday, 16:45",
    href: "/earnings",
    kind: "reward",
    readAt: null,
  },
  {
    id: "notif_003",
    title: "Profile update recommended",
    body: "Add car purchase intent to unlock more matching tasks.",
    createdAt: "12 May 2026",
    href: "/profile/edit",
    kind: "profile",
    readAt: "2026-05-13T08:15:00.000Z",
  },
  {
    id: "notif_004",
    title: "Cashout threshold nearby",
    body: "You need 80 more points before your next cashout.",
    createdAt: "10 May 2026",
    href: "/cashout",
    kind: "account",
    readAt: "2026-05-11T10:40:00.000Z",
  },
];

export const profile: Profile = {
  personal: {
    firstName: "Maya",
    lastName: "Patel",
    email: "maya.patel@example.com",
    phone: "+44 7700 900123",
    dob: "12 Mar 1992",
  },
  sections: [
    {
      title: "Household",
      fields: [
        {
          label: "Living situation",
          value: "Renting privately",
          key: "living",
        },
        {
          label: "Household income",
          value: "£35,000 – £49,999",
          key: "income",
        },
      ],
    },
    {
      title: "Work",
      fields: [
        {
          label: "Working status",
          value: "Full-time employed",
          key: "working",
        },
        { label: "Industry", value: "Finance & banking", key: "industry" },
      ],
    },
    {
      title: "Consumer profile",
      fields: [
        {
          label: "Streaming services",
          value: "Netflix, Disney+, Sky",
          key: "streaming",
        },
        { label: "Car purchase intent", value: null, key: "car" },
      ],
    },
  ],
  memberSince: "Apr 2026",
  commsPrefs: { email: true, sms: true, whatsapp: false },
};

export const profileFieldValues: ProfileFieldValues = {
  car_purchase_intent: null,
  household_income: "35000_49999",
  industry: "finance_banking",
  living_situation: "renting_privately",
  new_vs_used_preference: null,
  streaming_services: ["netflix", "disney_plus", "sky"],
  utility_providers: [],
  working_status: "full_time_employed",
};

export const profileFields: ProfileFieldsData = {
  sections: [
    {
      title: "Household",
      fields: [
        {
          fieldKey: "living_situation",
          label: "Living situation",
          options: [
            { label: "Renting privately", value: "renting_privately" },
            { label: "Own outright", value: "own_outright" },
            { label: "Own with mortgage", value: "own_with_mortgage" },
            {
              label: "Living with parents / family",
              value: "living_with_family",
            },
            { label: "Other", value: "other" },
          ],
          type: "select",
          width: "half",
        },
        {
          fieldKey: "household_income",
          label: "Household income",
          options: [
            { label: "GBP 35,000 - GBP 49,999", value: "35000_49999" },
            { label: "Under GBP 20,000", value: "under_20000" },
            { label: "GBP 20,000 - GBP 34,999", value: "20000_34999" },
            { label: "GBP 50,000 - GBP 74,999", value: "50000_74999" },
            { label: "GBP 75,000+", value: "75000_plus" },
            { label: "Prefer not to say", value: "prefer_not_to_say" },
          ],
          type: "select",
          width: "half",
        },
      ],
    },
    {
      title: "Subscriptions and utilities",
      fields: [
        {
          fieldKey: "streaming_services",
          label: "Streaming services",
          options: [
            { label: "Netflix", value: "netflix" },
            { label: "Amazon Prime", value: "amazon_prime" },
            { label: "Disney+", value: "disney_plus" },
            { label: "Apple TV+", value: "apple_tv_plus" },
            { label: "Sky", value: "sky" },
            { label: "None", value: "none" },
          ],
          type: "multiSelect",
          width: "full",
        },
        {
          fieldKey: "utility_providers",
          label: "Utility providers",
          options: [
            { label: "British Gas", value: "british_gas" },
            { label: "EDF", value: "edf" },
            { label: "E.ON", value: "eon" },
            { label: "Octopus", value: "octopus" },
            { label: "Scottish Power", value: "scottish_power" },
            { label: "Other", value: "other" },
          ],
          type: "multiSelect",
          width: "full",
        },
      ],
    },
    {
      title: "Work",
      fields: [
        {
          fieldKey: "working_status",
          label: "Working status",
          options: [
            { label: "Full-time employed", value: "full_time_employed" },
            { label: "Part-time employed", value: "part_time_employed" },
            { label: "Self-employed", value: "self_employed" },
            { label: "Retired", value: "retired" },
            { label: "Student", value: "student" },
            { label: "Unemployed", value: "unemployed" },
          ],
          type: "select",
          width: "half",
        },
        {
          fieldKey: "industry",
          label: "Industry",
          options: [
            { label: "Finance & banking", value: "finance_banking" },
            { label: "Technology", value: "technology" },
            { label: "Healthcare", value: "healthcare" },
            { label: "Education", value: "education" },
            { label: "Retail", value: "retail" },
            { label: "Other", value: "other" },
          ],
          type: "select",
          width: "half",
        },
      ],
    },
    {
      title: "Vehicle",
      fields: [
        {
          fieldKey: "car_purchase_intent",
          label: "Car purchase intent",
          options: [
            { label: "Within 6 months", value: "within_6_months" },
            { label: "6-12 months", value: "6_12_months" },
            { label: "1-2 years", value: "1_2_years" },
            { label: "2-4 years", value: "2_4_years" },
            { label: "Not planning to buy", value: "not_planning" },
          ],
          placeholder: "Select...",
          type: "select",
          width: "half",
        },
        {
          fieldKey: "new_vs_used_preference",
          label: "New vs used preference",
          options: [
            { label: "New", value: "new" },
            { label: "Used", value: "used" },
            { label: "Either", value: "either" },
          ],
          placeholder: "Select...",
          type: "select",
          width: "half",
        },
      ],
    },
  ],
};
